import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import classNames from 'classnames';
import { Icon } from 'tea-component';
import { getLanguage } from '@home/utils/common';
import SvgIcon from '@home/components/SvgIcon';
import TextBubbles from '@/pages/home/components/TextBubbles';
import dataFormat from '@home/utils/dbDataFormat/dataFormat';
import { getIsErrorValue } from '@/pages/dashboard/utils/utils';
import { useLocal } from '@/pages/dashboard/utils/customHook';
import Abnormal from '@/pages/dashboard/components/Abnormal';
import { FilterContext } from '@/pages/dashboard/components/PageContainer/context';
import { resolveDataKey } from '@/pages/dashboard/utils/kpiCardUtils';
import type {
  IRankProgressColumn,
  IRankProgressColumnsByDateType,
  IRankProgressListCardProps,
  IRankProgressMetricCell,
  IRankProgressRow,
} from './types';
import './index.scss';

/** 与 MetricsCardDod 中空值展示一致：class + 带空格的短横线 */
const DEFAULT_EMPTY_INNER = ' -';

type TRenderDodMetricResult = {
  body: React.ReactNode;
  isPositive: boolean;
  isZero: boolean;
  correctVal: boolean;
};

function getMetricRaw(m?: IRankProgressMetricCell): string | number | undefined {
  if (!m) {
    return undefined;
  }
  if (m.value !== undefined && m.value !== null) {
    return m.value;
  }
  return m.text;
}

/** 用于排序的可比值：尽量为 number，否则为 string；无效为 NaN（排到最后） */
function getSortComparable(
  row: IRankProgressRow,
  metricDataKey: string,
): number | string {
  const raw = getMetricRaw(row.metrics?.[metricDataKey]);
  if (raw === undefined || raw === null) {
    return Number.NaN;
  }
  if (getIsErrorValue(raw as string | number)) {
    return Number.NaN;
  }
  if (typeof raw === 'number') {
    return raw;
  }
  const n = Number(raw);
  if (!Number.isNaN(n) && String(raw).trim() !== '') {
    return n;
  }
  return String(raw);
}

/**
 * 进度条宽度 0–100：按占比类列数值（≤1 视为小数比率）；无效时用 row.progressPercent
 */
function getBarWidthPercent(
  row: IRankProgressRow,
  barMetricDataKey: string | undefined,
): number {
  const fallback = Math.min(
    100,
    Math.max(0, Number(row.progressPercent) || 0),
  );
  if (!barMetricDataKey) {
    return fallback;
  }
  const raw = getMetricRaw(row.metrics?.[barMetricDataKey]);
  if (getIsErrorValue(raw as string | number)) {
    return fallback;
  }
  const n = typeof raw === 'number' ? raw : Number(raw);
  if (!Number.isFinite(n) || n < 0) {
    return fallback;
  }
  const pct = n <= 1 ? n * 100 : n;
  return Math.min(100, Math.max(0, pct));
}

type TFilterValRangeTime = {
  rangeTime?: [unknown, unknown];
};

/** `rangeTime` 为 [start, end] 两项，去横线后不等即多日 */
export function isFilterDateRangeMultiDay(filterVal: unknown): boolean {
  const [a, b] = (filterVal as TFilterValRangeTime)?.rangeTime ?? [];
  return String(a).replace(/-/g, '') !== String(b).replace(/-/g, '');
}

/** 列配置 → `row.metrics` 对象上的字段名 */
function resolveMetricDataKey(col: IRankProgressColumn): string {
  if (col.metricSourceKey) {
    return col.metricSourceKey;
  }
  if (col.key === 'change') {
    return 'dod';
  }
  return col.key;
}

/** 配置里的 px 字段（数字或字符串） */
function resolveOptionalPx(
  v: number | string | undefined,
): number | undefined {
  if (v === undefined || v === null) {
    return undefined;
  }
  if (typeof v === 'number' && Number.isFinite(v) && v > 0) {
    return v;
  }
  if (typeof v === 'string') {
    const n = parseFloat(v.trim());
    if (Number.isFinite(n) && n > 0) {
      return n;
    }
  }
  return undefined;
}

function resolveColumnWidthPx(col: IRankProgressColumn): number | undefined {
  return resolveOptionalPx(col.columnWidth);
}

function resolveHeaderColumnTrackPx(
  col: IRankProgressColumn,
): number | undefined {
  return resolveOptionalPx(col.headerColumnWidth);
}

/** 未单独配 headerColumnWidth 时，表头轨宽对 columnWidth 的下限（标题+角标+排序） */
const METRIC_GRID_HEADER_TRACK_FLOOR_WHEN_VALUE_WIDTH_PX = 160;

/** 表体数值列轨宽对 columnWidth 的下限 */
const METRIC_GRID_BODY_TRACK_MIN_FOR_VALUE_PX = 72;

/** 固定栅格模式下未单独配置数值/表头宽的列的默认轨宽（Share / DoD / SDLW 等） */
const METRIC_GRID_DEFAULT_TRACK_PX = 100;

function needsStructuredMetricGrid(columns: IRankProgressColumn[]): boolean {
  return columns.some(
    col =>
      resolveColumnWidthPx(col) !== undefined ||
      resolveHeaderColumnTrackPx(col) !== undefined,
  );
}

function buildHeaderMetricsGridStyle(
  columns: IRankProgressColumn[],
): React.CSSProperties | undefined {
  if (!columns.length) {
    return undefined;
  }
  if (!needsStructuredMetricGrid(columns)) {
    return {
      gridTemplateColumns: `repeat(${columns.length}, minmax(72px, max-content))`,
    };
  }
  const template = columns
    .map(col => {
      const headerExplicit = resolveHeaderColumnTrackPx(col);
      if (headerExplicit !== undefined) {
        return `${headerExplicit}px`;
      }
      const valuePx = resolveColumnWidthPx(col);
      if (valuePx !== undefined) {
        return `${Math.max(
          valuePx,
          METRIC_GRID_HEADER_TRACK_FLOOR_WHEN_VALUE_WIDTH_PX,
        )}px`;
      }
      return `${METRIC_GRID_DEFAULT_TRACK_PX}px`;
    })
    .join(' ');
  return { gridTemplateColumns: template };
}

function buildBodyMetricsGridStyle(
  columns: IRankProgressColumn[],
): React.CSSProperties | undefined {
  if (!columns.length) {
    return undefined;
  }
  if (!needsStructuredMetricGrid(columns)) {
    return {
      gridTemplateColumns: `repeat(${columns.length}, minmax(72px, max-content))`,
    };
  }
  const template = columns
    .map(col => {
      const valuePx = resolveColumnWidthPx(col);
      if (valuePx !== undefined) {
        return `${Math.max(
          valuePx,
          METRIC_GRID_BODY_TRACK_MIN_FOR_VALUE_PX,
        )}px`;
      }
      return `${METRIC_GRID_DEFAULT_TRACK_PX}px`;
    })
    .join(' ');
  return { gridTemplateColumns: template };
}

function compareSortComparable(a: number | string, b: number | string): number {
  const aNan = typeof a === 'number' && Number.isNaN(a);
  const bNan = typeof b === 'number' && Number.isNaN(b);
  if (aNan && bNan) {
    return 0;
  }
  if (aNan) {
    return 1;
  }
  if (bNan) {
    return -1;
  }
  if (typeof a === 'number' && typeof b === 'number') {
    return a - b;
  }
  return String(a).localeCompare(String(b), undefined, { numeric: true });
}

/** 排序时视为「空」：与 asc/desc 无关，一律排在有值行之后 */
function isEmptySortComparable(v: number | string): boolean {
  if (typeof v === 'number' && Number.isNaN(v)) {
    return true;
  }
  if (typeof v === 'string' && v.trim() === '') {
    return true;
  }
  return false;
}

function MetricEmpty({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement {
  return <span className="data-null">{children}</span>;
}

function renderPlainMetric(
  m: IRankProgressMetricCell | undefined,
  col: IRankProgressColumn,
  emptyInner: string,
): React.ReactNode {
  const raw = getMetricRaw(m);
  if (getIsErrorValue(raw as string | number)) {
    return <MetricEmpty>{emptyInner}</MetricEmpty>;
  }
  if (col.formatRule) {
    return dataFormat({
      value: typeof raw === 'number' ? raw : String(raw),
      formatRule: col.formatRule,
      emptyValue: emptyInner.trim() || '-',
    });
  }
  const str = String(raw).trim();
  if (str === '') {
    return <MetricEmpty>{emptyInner}</MetricEmpty>;
  }
  return str;
}

function renderDodMetric(
  m: IRankProgressMetricCell | undefined,
  col: IRankProgressColumn,
  emptyInner: string,
): TRenderDodMetricResult {
  const raw = getMetricRaw(m);
  const correctVal = !getIsErrorValue(raw as string | number);
  const formatRule = col.formatRule ?? 'thousandsInit';
  if (!correctVal) {
    return {
      body: <MetricEmpty>{emptyInner}</MetricEmpty>,
      isPositive: false,
      isZero: false,
      correctVal: false,
    };
  }
  const num = Number(raw);
  const isPositive = num > 0;
  const isZero = num === 0;
  const formatted = dataFormat({
    value: raw as string | number,
    formatRule,
    emptyValue: emptyInner.trim() || '-',
  });

  let dodIcon: React.ReactNode = null;
  if (!col.hideDodIcon) {
    if (isZero) {
      dodIcon = <Icon type="trend-zero" />;
    } else if (isPositive) {
      dodIcon = (
        <SvgIcon className="overlay-card-body-icon" type="green-up-new" />
      );
    } else {
      dodIcon = (
        <SvgIcon className="overlay-card-body-icon" type="red-down-new" />
      );
    }
  }

  const body = (
    <>
      {col.showPlusOnPositive && isPositive ? '+' : null}
      {formatted}
      {dodIcon}
    </>
  );
  return { body, isPositive, isZero, correctVal: true };
}

const RankCell: React.FC<{ rank: number; }> = ({ rank }) => {
  if (rank >= 1 && rank <= 3) {
    return (
      <span
        className={classNames(
          'dbd-rank-progress-list__rank',
          'dbd-rank-progress-list__medal',
          `dbd-rank-progress-list__medal--${rank}`,
        )}
      >
        {rank}
      </span>
    );
  }
  return <span className="dbd-rank-progress-list__rank">{rank}</span>;
};

const RowLeadingIcon: React.FC<{
  row: IRankProgressRow;
  renderRowIcon?: IRankProgressListCardProps['renderRowIcon'];
}> = ({ row, renderRowIcon }) => {
  if (renderRowIcon) {
    return <>{renderRowIcon(row)}</>;
  }
  if (row.iconUrl) {
    return (
      <span className="dbd-rank-progress-list__icon">
        <img
          src={row.iconUrl}
          alt=""
          className="dbd-rank-progress-list__icon-img"
        />
      </span>
    );
  }
  if (row.iconType) {
    return (
      <span className="dbd-rank-progress-list__icon">
        <SvgIcon type={row.iconType} className="dbd-rank-progress-list__icon-svg" />
      </span>
    );
  }
  return <span className="dbd-rank-progress-list__icon" aria-hidden />;
};

function pickColumnsByDateType(
  fromProps?: IRankProgressColumnsByDateType,
  fromData?: IRankProgressColumnsByDateType,
): IRankProgressColumnsByDateType | undefined {
  const ok = (x?: IRankProgressColumnsByDateType) =>
    Boolean(x?.singleDate?.length && x?.rangeDate?.length);
  if (ok(fromProps)) {
    return fromProps;
  }
  if (ok(fromData)) {
    return fromData;
  }
  return undefined;
}

/**
 * 带进度条与多指标列的排名列表；行首 icon 支持任意图片 URL / SvgIcon，不限国家旗。
 */
const RankProgressListCard: React.FC<IRankProgressListCardProps> = ({
  className,
  style,
  titleCn,
  titleEn,
  subTitleCn,
  subTitleEn,
  rowLabelCn,
  rowLabelEn,
  columns: columnsProp,
  rows: rowsProp,
  columnsByDateType: columnsByDateTypeProp,
  columnsWhenDateRange: columnsWhenDateRangeProp,
  data,
  dataKey: dataKeyProp,
  progressColor = '#5086F3',
  progressBarColumnKey = 'share',
  emptyValue = DEFAULT_EMPTY_INNER,
  renderRowIcon,
  onSortChange,
}) => {
  const { isEN } = useLocal();
  const { filterVal } = useContext(FilterContext);

  const dataKey = useMemo(() => {
    if (dataKeyProp) {
      return dataKeyProp;
    }
    return resolveDataKey(data, undefined);
  }, [data, dataKeyProp]);
  const serviceBlock = dataKey ? data?.[dataKey] : undefined;
  const servicePayload = serviceBlock?.data;
  const serviceLoading = Boolean(serviceBlock?.loading);
  const serviceErrorMsg = serviceBlock?.errorMsg;

  const columnsByDateType = pickColumnsByDateType(
    columnsByDateTypeProp,
    servicePayload?.columnsByDateType ?? data?.columnsByDateType,
  );

  const legacyRangeColumns =
    columnsWhenDateRangeProp?.length
      ? columnsWhenDateRangeProp
      : servicePayload?.columnsWhenDateRange ?? data?.columnsWhenDateRange;

  const isDateRangeMultiDay = useMemo(
    () => isFilterDateRangeMultiDay(filterVal),
    [filterVal],
  );

  const baseColumns = useMemo(() => {
    if (columnsByDateType?.singleDate?.length) {
      return columnsByDateType.singleDate;
    }
    return columnsProp?.length
      ? columnsProp
      : servicePayload?.columns ?? data?.columns ?? [];
  }, [columnsByDateType, columnsProp, data?.columns, servicePayload?.columns]);

  const columns = useMemo(() => {
    if (
      columnsByDateType?.singleDate?.length &&
      columnsByDateType.rangeDate?.length
    ) {
      return isDateRangeMultiDay
        ? columnsByDateType.rangeDate
        : columnsByDateType.singleDate;
    }
    if (isDateRangeMultiDay && legacyRangeColumns?.length) {
      return legacyRangeColumns;
    }
    return baseColumns;
  }, [
    columnsByDateType,
    isDateRangeMultiDay,
    legacyRangeColumns,
    baseColumns,
  ]);

  const rows = useMemo(() => {
    if (rowsProp?.length) {
      return rowsProp;
    }
    const fromService = servicePayload?.rows;
    if (fromService?.length) {
      return fromService;
    }
    return data?.rows ?? [];
  }, [rowsProp, servicePayload?.rows, data?.rows]);

  const columnKeySig = useMemo(
    () => columns.map(c => c.key).join(','),
    [columns],
  );

  const [sortState, setSortState] = useState<{
    key: string;
    order: 'asc' | 'desc';
  } | null>(null);

  const handleSortToggle = useCallback((key: string) => {
    setSortState(prev => {
      if (prev?.key === key) {
        return {
          key,
          order: prev.order === 'asc' ? 'desc' : 'asc',
        };
      }
      return { key, order: 'desc' };
    });
  }, []);

  useEffect(() => {
    setSortState(null);
  }, [columnKeySig, isDateRangeMultiDay]);

  useEffect(() => {
    if (sortState) {
      onSortChange?.({ sortKey: sortState.key, sortOrder: sortState.order });
    }
  }, [sortState, onSortChange]);

  const sortedRows = useMemo(() => {
    if (!sortState) {
      return rows;
    }
    const colIndex = columns.findIndex(c => c.key === sortState.key);
    if (colIndex < 0) {
      return rows;
    }
    const metricDataKey = resolveMetricDataKey(columns[colIndex]);
    const mult = sortState.order === 'asc' ? 1 : -1;
    const withIdx = rows.map((r, i) => ({ r, i }));
    withIdx.sort((a, b) => {
      const va = getSortComparable(a.r, metricDataKey);
      const vb = getSortComparable(b.r, metricDataKey);
      const aEmpty = isEmptySortComparable(va);
      const bEmpty = isEmptySortComparable(vb);
      if (aEmpty !== bEmpty) {
        return aEmpty ? 1 : -1;
      }
      if (aEmpty && bEmpty) {
        return a.i - b.i;
      }
      const c = compareSortComparable(va, vb);
      if (c !== 0) {
        return mult * c;
      }
      return a.i - b.i;
    });
    return withIdx.map(x => x.r);
  }, [rows, sortState, columns]);

  const title = useMemo(
    () => getLanguage(isEN, titleEn ?? '', titleCn ?? ''),
    [isEN, titleEn, titleCn],
  );
  const subTitle = useMemo(
    () => getLanguage(isEN, subTitleEn ?? '', subTitleCn ?? ''),
    [isEN, subTitleEn, subTitleCn],
  );
  const rowLabel = useMemo(
    () => getLanguage(isEN, rowLabelEn ?? '', rowLabelCn ?? '').trim(),
    [isEN, rowLabelEn, rowLabelCn],
  );
  const isStackedHeader = rowLabel.length > 0;

  const gridColsHeader = useMemo(
    () => buildHeaderMetricsGridStyle(columns),
    [columns],
  );

  const gridColsBody = useMemo(
    () => buildBodyMetricsGridStyle(columns),
    [columns],
  );

  const metricCellConstraintStyle = (
    col: IRankProgressColumn,
  ): React.CSSProperties | undefined => {
    const px = resolveColumnWidthPx(col);
    if (px === undefined) {
      return undefined;
    }
    return {
      maxWidth: px,
      justifySelf: 'end',
    };
  };

  const progressBarMetricKey = useMemo(() => {
    const di = columns.findIndex(c => c.key === progressBarColumnKey);
    if (di < 0) {
      return undefined;
    }
    return resolveMetricDataKey(columns[di]);
  }, [columns, progressBarColumnKey]);

  if (!columns.length) {
    return null;
  }

  const metricHeaderCells = columns.map(col => {
    const isSortActive = sortState?.key === col.key;
    const label = getLanguage(isEN, col.labelEn, col.labelCn);
    const badgeNode = col.badge ? (
      <span className="dbd-rank-progress-list__badge">
        {getLanguage(isEN, col.badge.textEn, col.badge.textCn)}
      </span>
    ) : null;

    let sortIconType: 'sort-icon' | 'arrow-other-down' | 'arrow-other-up' =
      'sort-icon';
    if (isSortActive && sortState) {
      sortIconType =
        sortState.order === 'desc' ? 'arrow-other-down' : 'arrow-other-up';
    }
    const sortIconActiveStyle: React.CSSProperties | undefined = isSortActive
      ? { fontSize: '18px', marginTop: '-2px' }
      : undefined;

    return (
      <div
        key={col.key}
        className={classNames('dbd-rank-progress-list__th', {
          'dbd-rank-progress-list__th--sortable': col.sortable,
        })}
      >
        {col.sortable ? (
          <div
            className={classNames('dbd-rank-progress-list__th-sort', {
              'dbd-rank-progress-list__th-sort--selected': isSortActive,
            })}
            role="button"
            tabIndex={0}
            onClick={() => handleSortToggle(col.key)}
            onKeyDown={(e: React.KeyboardEvent) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSortToggle(col.key);
              }
            }}
            aria-label={`${label}，排序`}
          >
            <span className="dbd-rank-progress-list__th-sort-label">
              {label}
            </span>
            {badgeNode}
            <SvgIcon
              type={sortIconType}
              className="dbd-rank-progress-list__sort-icon"
              style={sortIconActiveStyle}
            />
          </div>
        ) : (
          <>
            <span>{label}</span>
            {badgeNode}
          </>
        )}
      </div>
    );
  });

  /** 加载 / 失败 / 无数据时只保留主副标题，不展示表头列（维度名+指标列），异常区紧跟标题下 */
  const suppressColumnHeaders =
    Boolean(dataKey) &&
    (serviceLoading ||
      Boolean(serviceErrorMsg) ||
      (!serviceLoading && !serviceErrorMsg && sortedRows.length === 0));

  const cardHeader = (
    <div
      className={classNames('dbd-rank-progress-list__header', {
        'dbd-rank-progress-list__header--stacked': isStackedHeader,
      })}
    >
      {isStackedHeader ? (
        <>
          <div className="dbd-rank-progress-list__header-title-row">
            <div className="dbd-rank-progress-list__titles">
              {title ? (
                <span className="dbd-rank-progress-list__title">{title}</span>
              ) : null}
              {subTitle ? (
                <span className="dbd-rank-progress-list__sub">{subTitle}</span>
              ) : null}
            </div>
          </div>
          {!suppressColumnHeaders ? (
            <div className="dbd-rank-progress-list__header-columns-row">
              <div className="dbd-rank-progress-list__row-heading">
                {rowLabel}
              </div>
              <div className="dbd-rank-progress-list__bar-spacer" aria-hidden />
              <div
                className="dbd-rank-progress-list__header-metrics"
                style={gridColsHeader}
              >
                {metricHeaderCells}
              </div>
            </div>
          ) : null}
        </>
      ) : (
        <>
          <div className="dbd-rank-progress-list__title-block">
            <div className="dbd-rank-progress-list__titles">
              {title ? (
                <span className="dbd-rank-progress-list__title">{title}</span>
              ) : null}
              {subTitle ? (
                <span className="dbd-rank-progress-list__sub">{subTitle}</span>
              ) : null}
            </div>
          </div>
          {!suppressColumnHeaders ? (
            <>
              <div className="dbd-rank-progress-list__bar-spacer" aria-hidden />
              <div
                className="dbd-rank-progress-list__header-metrics"
                style={gridColsHeader}
              >
                {metricHeaderCells}
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );

  const cardBody = (
    <div className="dbd-rank-progress-list__body">
      {sortedRows.map((row, idx) => (
        <div
          key={`${row.rank}-${row.name}-${idx}`}
          className="dbd-rank-progress-list__row"
        >
          <div className="dbd-rank-progress-list__row-left">
            <RankCell rank={sortState ? idx + 1 : row.rank} />
            <RowLeadingIcon row={row} renderRowIcon={renderRowIcon} />
            <TextBubbles
              className="dbd-sales-breakdown-flex-name"
              text={row.name || emptyValue.trim() || '-'}
            />
          </div>
          <div className="dbd-rank-progress-list__bar-wrap">
            <div
              className="dbd-rank-progress-list__bar-fill"
              style={{
                width: `${getBarWidthPercent(row, progressBarMetricKey)}%`,
                background: progressColor,
              }}
            />
          </div>
          <div
            className="dbd-rank-progress-list__metrics"
            style={gridColsBody}
          >
            {columns.map((col, mi) => {
              const metricDataKey = resolveMetricDataKey(col);
              const m = row.metrics?.[metricDataKey];
              const variant = col.variant ?? 'plain';
              if (variant === 'dod') {
                const { body, isPositive, isZero, correctVal } = renderDodMetric(
                  m,
                  col,
                  emptyValue,
                );
                return (
                  <span
                    key={col.key ?? mi}
                    className={classNames(
                      'dbd-rank-progress-list__metric',
                      'dbd-rank-progress-list__metric--dod',
                      {
                        'dbd-rank-progress-list__metric--fixed-width':
                          resolveColumnWidthPx(col) !== undefined,
                        'text-green': correctVal && isPositive,
                        'text-red': correctVal && !isPositive && !isZero,
                        'text-gray': correctVal && isZero,
                      },
                    )}
                    style={metricCellConstraintStyle(col)}
                  >
                    {body}
                  </span>
                );
              }
              const display = renderPlainMetric(m, col, emptyValue);
              return (
                <span
                  key={col.key ?? mi}
                  className={classNames('dbd-rank-progress-list__metric', {
                    'dbd-rank-progress-list__metric--fixed-width':
                      resolveColumnWidthPx(col) !== undefined,
                    'dbd-rank-progress-list__metric--up': m?.trend === 'up',
                    'dbd-rank-progress-list__metric--down': m?.trend === 'down',
                  })}
                  style={metricCellConstraintStyle(col)}
                >
                  {display}
                  {m?.trend === 'up' ? (
                    <span className="dbd-rank-progress-list__arrow">↑</span>
                  ) : null}
                  {m?.trend === 'down' ? (
                    <span className="dbd-rank-progress-list__arrow">↓</span>
                  ) : null}
                </span>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );

  /** 标题始终在卡片内展示；仅列表区域走 Abnormal（加载/失败/无数据） */
  return (
    <div
      className={classNames('dbd-rank-progress-list', className)}
      style={style}
    >
      {cardHeader}
      {dataKey ? (
        <Abnormal
          isLoading={serviceLoading}
          isError={Boolean(serviceErrorMsg)}
          isNoData={
            !serviceLoading &&
            !serviceErrorMsg &&
            sortedRows.length === 0
          }
          // failedMsg={serviceErrorMsg || undefined}
          style={{ minHeight: 200 }}
          component={cardBody}
        />
      ) : (
        cardBody
      )}
    </div>
  );
};

export default RankProgressListCard;
export type {
  IRankProgressColumn,
  IRankProgressColumnsByDateType,
  IRankProgressMetricCell,
  IRankProgressRow,
  IRankProgressListCardProps,
} from './types';
